/**
 * Endpoint de trazado de egresos — backend real.
 *
 * El polling del status se hace en el backend (poller con setInterval) y se
 * lee de `traza_egreso`. El front solo dispara el inicio y consulta el estado
 * via `useTareasPendientes` (que viene mergeado con la última traza por
 * referencia).
 */

import { apiFetch, ApiError } from '@/api/http';
import { z } from 'zod';
import type { TareaTipo } from '@/api/contracts/tareas';
import {
  TrazaJobDetalleSchema,
  type TrazaJobDetalle,
} from '@/api/contracts/traza-job-detalle';
import { ESTADOS_TRAZA_JOB } from '@/domain/types';

export const IniciarTrazaResponseSchema = z.object({
  id: z.number(),
  referencia: z.string(),
  jobId: z.number(),
  status: z.enum(ESTADOS_TRAZA_JOB),
  totalChunks: z.number().nullable().optional(),
  totalItems: z.number().nullable().optional(),
});
export type IniciarTrazaResponse = z.infer<typeof IniciarTrazaResponseSchema>;

export type IniciarTrazaTipo = Exclude<TareaTipo, 'otro'>;

/**
 * Pide al backend el payload que se le mandaría a riga-traza-v2 para esta
 * combinación de (referencia, tipo, glnId), SIN dispararlo. Pensado para el
 * modal de debug "ver payload" del front.
 */
export interface TrazaStats {
  /** Trazas de egreso en estado error/failed para el GLN activo (última por referencia). */
  errores: number;
  /** Despachos trazados OK cuyo cierre de tarea en el ERP (cumplir-tarea) quedó en error. */
  erroresCierreErp: number;
  /** Items con `anmat_transaction_id` no null creados hoy desde el GLN activo. */
  seriesTrazadasHoy: number;
}

/** Stats del KPI del dashboard para el GLN activo. */
export async function getTrazaStats(glnId: string | number): Promise<TrazaStats> {
  const params = new URLSearchParams({ glnId: String(Number(glnId)) });
  return apiFetch<TrazaStats>(`/traza/stats?${params.toString()}`, { auth: true });
}

/** Un evento ANMAT posible para un par (actor origen, actor destino). */
export interface EventoDisponible {
  eventCode: number;
  descripcion: string;
  esDestruccion: boolean;
  esDevolucion: boolean;
}

/** Respuesta de /traza/eventos-disponibles. Si `eventos` viene vacío, `motivo`
 *  explica por qué no se pudo resolver (para mostrarlo en el combo). */
export interface EventosDisponiblesResult {
  eventos: EventoDisponible[];
  motivo?:
    | 'ORIGEN_SIN_TIPO_ACTOR'
    | 'SIN_GLN_DESTINO'
    | 'DESTINO_SIN_TIPO_ACTOR'
    | 'DESTINO_TIPO_ACTOR_NO_MAPEADO';
  origen?: { agentTypeId: number; gln: string };
  destino?: {
    agentTypeId: number;
    tipoAgente: string | null;
    idTipoAgenteAnmat?: string | null;
    gln: string;
  };
}

/**
 * Lista los eventos ANMAT posibles para trazar el egreso de `referencia`, según
 * el par (GLN activo = origen, GLN destino del comprobante = destino). Resuelve
 * el tipo de actor destino consultando el SNT en vivo. No dispara traza.
 */
export async function getEventosDisponibles(
  referencia: string,
  glnId: string | number,
): Promise<EventosDisponiblesResult> {
  const params = new URLSearchParams({
    referencia,
    glnId: String(Number(glnId)),
  });
  return apiFetch<EventosDisponiblesResult>(
    `/traza/eventos-disponibles?${params.toString()}`,
    { auth: true },
  );
}

export async function previewPayloadEgreso(
  referencia: string,
  tipo: IniciarTrazaTipo,
  glnId: string | number,
  idEvento?: number | null,
): Promise<unknown> {
  const params = new URLSearchParams({
    referencia,
    tipo,
    glnId: String(Number(glnId)),
  });
  // El backend valida idEvento con coerce+positive → mandar null rompería.
  // Solo lo agregamos si hay un evento elegido.
  if (idEvento != null) params.set('idEvento', String(idEvento));
  return apiFetch<unknown>(`/traza/preview-egreso?${params.toString()}`, { auth: true });
}

export async function iniciarTrazaEgreso(
  referencia: string,
  tipo: IniciarTrazaTipo,
  glnId: string | number,
  erpId: string | null,
  idEvento?: number | null,
  eventoDesc?: string | null,
): Promise<IniciarTrazaResponse> {
  // Igual que en el preview: idEvento se OMITE si es null (no se manda null,
  // porque el schema lo coerciona a número positivo y fallaría).
  const body: Record<string, unknown> = {
    referencia,
    tipo,
    glnId: Number(glnId),
    erpId: erpId ?? null,
  };
  if (idEvento != null) body.idEvento = idEvento;
  // Descripción del evento — snapshot para mostrar el despacho trazado sin
  // re-consultar el catálogo. Se omite si no hay.
  if (eventoDesc) body.eventoDesc = eventoDesc;
  const data = await apiFetch<unknown>('/traza/egreso', {
    method: 'POST',
    body,
    auth: true,
  });
  return IniciarTrazaResponseSchema.parse(data);
}

/** Resultado del re-disparo de cierre de tarea en Plataforma. */
export interface CumplirTareaResult {
  ok: boolean;
  skipped?: boolean;
  ejecucionOk?: boolean;
  mensajeError?: string | null;
  error?: string;
}

/**
 * Re-dispara el cierre de tarea en Plataforma (POST /editar/cumplir-tarea)
 * para una referencia ya trazada con éxito. Reintento / test manual.
 */
export async function cumplirTareaErp(referencia: string): Promise<CumplirTareaResult> {
  return apiFetch<CumplirTareaResult>('/traza/cumplir-tarea', {
    method: 'POST',
    body: { referencia },
    auth: true,
  });
}

/** Tipos válidos del polling de jobs (lectura desde nuestra DB). */
export type TrazaJobTipo = 'egreso' | 'ingreso';

/**
 * Lee la última fila de traza_job para (referencia, tipo) con TODA la info
 * de atribución, payloads y códigos ANMAT. Pega solo al backend, NO a riga.
 * Devuelve null si no hay traza todavía (404 silencioso).
 */
export async function getTrazaJobByReferencia(
  referencia: string,
  tipo: TrazaJobTipo,
): Promise<TrazaJobDetalle | null> {
  try {
    const data = await apiFetch<unknown>(
      `/traza/job?referencia=${encodeURIComponent(referencia)}&tipo=${encodeURIComponent(tipo)}`,
      { auth: true },
    );
    return TrazaJobDetalleSchema.parse(data);
  } catch (err) {
    if (err instanceof ApiError && err.code === 'TRAZA_NOT_FOUND') return null;
    throw err;
  }
}
